import { WidgetConfig } from "./remote_config";

/**
 * Teaser bubble: a small message that pops up next to the closed launcher
 * a configured number of seconds after the widget initialised
 * (`chatbot.teaser_delay_seconds`, 0 = off). Display only: showing it
 * sends nothing to the backend. Clicking it opens the chat exactly like
 * the launcher does, and that open is what starts the chat session (and
 * its conversation), the same as it always has.
 *
 * The decisions live here, away from the DOM, so they can be tested.
 */

/** setTimeout's ceiling (~24.8 days). A longer delay would overflow and
 *  fire at once, so it is clamped: effectively "never". */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Milliseconds still to wait before showing the teaser, or undefined when
 * it is off. `elapsedMs` is the time already spent since widget init (the
 * config request happens in between), so the delay counts from init, not
 * from the moment the launcher was mounted.
 *
 * Off when the server didn't send a delay (a services instance that
 * predates the option) or sent anything that isn't a positive number.
 */
export function teaserDelayMs(
    seconds: unknown,
    elapsedMs = 0,
): number | undefined {
    if (typeof seconds !== "number" || !Number.isFinite(seconds)) {
        return undefined;
    }
    if (seconds <= 0) return undefined;
    const remaining = seconds * 1000 - Math.max(0, elapsedMs);
    return Math.min(Math.max(0, remaining), MAX_TIMEOUT_MS);
}

/**
 * The teaser's text: the operator's own message, or the greeting when
 * that is empty. Empty when both are — then there is nothing to show.
 */
export function teaserText(
    config: Pick<WidgetConfig, "teaser_message" | "greeting">,
): string {
    const own =
        typeof config.teaser_message === "string"
            ? config.teaser_message.trim()
            : "";
    if (own) return own;
    return typeof config.greeting === "string" ? config.greeting.trim() : "";
}

/** How often the teaser looks again at what the page shows where it
 *  sits: while it waits for that spot to clear, and while it is shown. */
export const TEASER_RECHECK_MS = 1000;

/** What the overlap check needs from the page. The launcher passes the
 *  real DOM; tests pass a few plain objects. */
export interface TeaserProbe<E extends { parentElement: E | null }> {
    /** Everything under a viewport point, topmost first
     *  (document.elementsFromPoint). */
    elementsAt(x: number, y: number): E[];
    /** Part of the widget itself (launcher, teaser). */
    isOwn(el: E): boolean;
    /** Stays put on screen while the page scrolls: computed position
     *  fixed or sticky. Body and html never count. */
    staysOnScreen(el: E): boolean;
}

/**
 * Whether the teaser, placed at `rect`, would sit on top of something
 * the site itself keeps on screen there: a cookie banner still waiting
 * for an answer, a modal and its backdrop, a sticky bar. Then the teaser
 * has to wait. On a phone it sits right where a stacked cookie banner
 * has its buttons, and a tap meant for "Alleen noodzakelijke" would land
 * on the teaser and open the chat instead.
 *
 * Probes the corners and the centre: at each, the topmost element that
 * isn't ours, and every ancestor of it.
 */
export function teaserCoversHostOverlay<E extends { parentElement: E | null }>(
    rect: { left: number; top: number; right: number; bottom: number },
    probe: TeaserProbe<E>,
): boolean {
    const inset = 2;
    const left = rect.left + inset;
    const right = rect.right - inset;
    const top = rect.top + inset;
    const bottom = rect.bottom - inset;
    const points: [number, number][] = [
        [left, top],
        [right, top],
        [left, bottom],
        [right, bottom],
        [(rect.left + rect.right) / 2, (rect.top + rect.bottom) / 2],
    ];
    for (const [x, y] of points) {
        const hit = probe.elementsAt(x, y).find((el) => !probe.isOwn(el));
        for (let el = hit ?? null; el; el = el.parentElement) {
            if (probe.staysOnScreen(el)) return true;
        }
    }
    return false;
}
